/**
 * Merkle Tree for RewardDistributor
 * Based on OpenZeppelin MerkleTree conventions
 */

import { keccak256, encodeAbiParameters, parseAbiParameters, type Hex } from 'viem';

export interface MerkleLeaf {
  leafIndex: number;
  account: string;
  amount: bigint;
}

export interface MerkleProof {
  leafIndex: number;
  account: string;
  amount: string;
  proof: Hex[];
}

/**
 * Hash a leaf node: keccak256(abi.encode(leafIndex, account, amount))
 * Uses abi.encode (not encodePacked) to match RewardDistributor contract
 */
function hashLeaf(leaf: MerkleLeaf): Hex {
  return keccak256(
    encodeAbiParameters(
      parseAbiParameters('uint256, address, uint256'),
      [BigInt(leaf.leafIndex), leaf.account as `0x${string}`, leaf.amount]
    )
  );
}

/**
 * Hash two nodes together (sorted for consistency)
 * Uses abi.encode to match OpenZeppelin MerkleProof convention
 */
function hashPair(a: Hex, b: Hex): Hex {
  const sorted = a < b ? [a, b] : [b, a];
  return keccak256(
    encodeAbiParameters(
      parseAbiParameters('bytes32, bytes32'),
      sorted as [Hex, Hex]
    )
  );
}

/**
 * Build Merkle tree and return root + proofs
 */
export function buildMerkleTree(leaves: MerkleLeaf[]): {
  root: Hex;
  proofs: MerkleProof[];
} {
  if (leaves.length === 0) {
    throw new Error('Cannot build Merkle tree with no leaves');
  }

  // Hash all leaves
  const hashedLeaves = leaves.map((leaf) => ({
    leaf,
    hash: hashLeaf(leaf),
  }));

  // Sort by hash for deterministic ordering
  hashedLeaves.sort((a, b) => (a.hash < b.hash ? -1 : 1));

  // Build tree layers
  const layers: Hex[][] = [hashedLeaves.map((l) => l.hash)];

  while (layers[layers.length - 1]!.length > 1) {
    const currentLayer = layers[layers.length - 1]!;
    const nextLayer: Hex[] = [];

    for (let i = 0; i < currentLayer.length; i += 2) {
      if (i + 1 < currentLayer.length) {
        nextLayer.push(hashPair(currentLayer[i]!, currentLayer[i + 1]!));
      } else {
        // Odd number of nodes - promote the last one
        nextLayer.push(currentLayer[i]!);
      }
    }

    layers.push(nextLayer);
  }

  const root = layers[layers.length - 1]![0]!;

  // Generate proofs for each leaf
  const proofs: MerkleProof[] = hashedLeaves.map(({ leaf, hash }) => {
    const proof: Hex[] = [];
    let currentHash = hash;

    for (let layerIndex = 0; layerIndex < layers.length - 1; layerIndex++) {
      const layer = layers[layerIndex];
      const index = layer.indexOf(currentHash);

      if (index === -1) {
        throw new Error('Hash not found in layer');
      }

      // Get sibling
      const siblingIndex = index % 2 === 0 ? index + 1 : index - 1;
      if (siblingIndex < layer.length) {
        proof.push(layer[siblingIndex]);
      }

      // Move to next layer
      const nextLayer = layers[layerIndex + 1];
      currentHash = nextLayer[Math.floor(index / 2)];
    }

    return {
      leafIndex: leaf.leafIndex,
      account: leaf.account,
      amount: leaf.amount.toString(),
      proof,
    };
  });

  return { root, proofs };
}

/**
 * Verify a proof against a root
 */
export function verifyProof(
  root: Hex,
  leafIndex: number,
  account: string,
  amount: bigint,
  proof: Hex[]
): boolean {
  let hash = hashLeaf({ leafIndex, account, amount });

  for (const sibling of proof) {
    hash = hashPair(hash, sibling);
  }

  return hash === root;
}
